import { Component, effect, inject, input, model } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { UtilsService } from '@dxc/data-access';
import { Tab } from '@shared/types';
import { HlmButtonDirective } from '@spartan-ng/button';
import {
  findTabByLabelOrDefault,
  getTabVariant,
  tabClasses
} from './tabs-base';

/**
 * Component responsible for rendering and managing navigation tabs.
 *
 * @input tabs - The list of tabs to be displayed, each containing a label and a route.
 * @model activeTab - A model representing the currently active tab.
 *
 * This component listens to route changes to update the active tab accordingly and uses a utility service for string formatting.
 */
@Component({
  selector: 'dxc-tabs',
  imports: [RouterLink, HlmButtonDirective],
  template: `
    <!-- Container for navigation tabs with responsive design and custom scrollbar styling -->
    <nav role="tablist" aria-label="tabs" [class]="tabClasses[this.variant()]">
      <!-- Navigation tabs with horizontal scrolling -->
      <div [class]="tabClasses.navDiv">
        @for (tab of tabs(); track tab.label) {
          <a
            hlmBtn
            class="text-sm"
            role="tab"
            size="sm"
            [attr.aria-selected]="tab?.label === activeTab()?.label"
            [attr.tabindex]="tab?.label === activeTab()?.label ? 0 : -1"
            [routerLink]="tab?.route"
            [variant]="getTabVariant(tab)"
          >
            {{ utilsService.formatString(tab?.label || '') }}</a
          >
        }
      </div>
    </nav>
  `
})
export class TabsComponent {
  public tabs = input.required<Tab[]>();
  public variant = input<'page' | 'table'>('page');
  public activeTab = model<Tab>();
  public level = input<'parent' | 'child'>('parent');
  protected utilsService = inject(UtilsService);
  private activatedRoute = inject(ActivatedRoute);
  protected routeTab: string = this.activatedRoute.snapshot.data['tab'];
  protected routeChildTab: string =
    this.activatedRoute.snapshot.data['childTab'];
  protected tabClasses = tabClasses;

  constructor() {
    // Effect hook to set the active tab based on the current route or default to the first tab.
    effect(() => {
      if (this.level() === 'child') {
        this.activeTab.set(
          findTabByLabelOrDefault(this.tabs(), this.routeChildTab)
        );
      } else {
        this.activeTab.set(findTabByLabelOrDefault(this.tabs(), this.routeTab));
      }
    });
  }

  /**
   * Determines the variant of the tab based on whether it is the active tab.
   *
   * @param tab - The tab to determine the variant for.
   * @returns hlmBtn variant.
   */
  protected getTabVariant(tab: Tab): 'outline' | 'link' | 'tab' | 'activeTab' {
    return getTabVariant(this.variant(), tab, this.activeTab());
  }
}
